import { useEffect, useState } from "react";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import swal from "sweetalert";
import {
  LoadingSpinner,
  customOption,
  customSingleValue,
  isUrdu,
} from "../../../../constants/const";
import { GetLanguageString } from "../../../helper/Components";
import { GetSelectedLanguage } from "../../../helper/Method";
import useForm from "../../../shared/useForm";
import Select from "react-select";
import { ApiFinance } from "../ApiFinance";

const EditFinanceAccountCategory = ({
  setEditModal,
  setReload,
  reload,
  accountCategoryValues,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [financeAccountCategoryTypes, setFinanceAccountCategoryTypes] =
    useState([]);
  const [
    selectedFinanceAccountCategoryType,
    setSelectedFinanceAccountCategoryType,
  ] = useState(null);
  const [errors, setErrors] = useState({
    category: "",
  });
  const { values, handleInputChange } = useForm(accountCategoryValues);
  const toggle = () => setEditModal(false);

  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleDrag = (e, ui) => {
    const { x, y } = position;
    setPosition({ x: x + ui.deltaX, y: y + ui.deltaY });
  };

  useEffect(() => {
    setLoading(true);
    ApiFinance()
      .GetFinanceAccountCategoryTypes()
      .then((res) => {
        setFinanceAccountCategoryTypes(
          res.data.map((item) => ({
            value: item.id,
            label: item.type,
          }))
        );
        setSelectedFinanceAccountCategoryType(
          res.data
            .map((item) => ({ value: item.id, label: item.type }))
            .find((item) => item.value === values.accountCategoryType.value)
        );

        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        swal({
          title: err,
          text: err.response.data,
          icon: "error",
          buttons: "OK",
        });
        setLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  const validate = () => {
    let temp = {};
    temp.category = values.category ? "" : t("common_field_required_error");
    temp.accountCategoryTypeId = selectedFinanceAccountCategoryType
      ? ""
      : t("common_field_required_error");
    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const preparePayload = () => {
    const payload = { ...values };
    payload.code = payload.code ?? "";
    payload.accountCategoryTypeId = selectedFinanceAccountCategoryType.value;
    return payload;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const payload = preparePayload();
    ApiFinance()
      .UpdateFinanceAccountCategory(values.id, payload)
      .then(() => {
        swal({
          title: t("finance_finance_account_category_updated"),
          className: GetSelectedLanguage() === "en" ? "" : "ur",
          icon: "success",
          buttons: "OK",
        });
        setReload(!reload);
        toggle();
      })
      .catch((err) => {
        swal({
          title: err.message || "Something went wrong",
          text: err.response?.data || "",
          icon: "error",
          buttons: "OK",
        });
        toggle();
        console.error(err);
      })
      .finally(() => setLoading(false));
  };

  return (
    <div>
      <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
        <Modal isOpen={true} className="inmodal" autoFocus={false}>
          <ModalHeader className="modal-title" tag="h4">
            {
              <GetLanguageString props="finance_tab_finance_account_categories_edit" />
            }
          </ModalHeader>
          {!loading ? (
            <Form onSubmit={handleSubmit}>
              <ModalBody>
                <FormGroup>
                  <Label>
                    {<GetLanguageString props="finance_tab_category" />}
                  </Label>
                  <Input
                    autoFocus
                    type="text"
                    name="category"
                    className={
                      isUrdu(values.category) ? "ur no-drag" : "no-drag"
                    }
                    value={values.category}
                    onChange={handleInputChange}
                    {...(errors.category && { invalid: true })}
                  />
                  {errors.category && (
                    <FormFeedback>
                      <h4>{errors.category}</h4>
                    </FormFeedback>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label className="col-form-label">
                    {
                      <GetLanguageString props="finance_tab_finance_account_category_select_type" />
                    }
                  </Label>
                  <Select
                    components={{
                      Option: customOption,
                      SingleValue: customSingleValue,
                    }}
                    value={selectedFinanceAccountCategoryType}
                    options={financeAccountCategoryTypes}
                    className="basic-single no-drag"
                    classNamePrefix="select"
                    onChange={(v) => {
                      setSelectedFinanceAccountCategoryType(v);
                      setErrors({
                        ...errors,
                        accountCategoryTypeId: "",
                      });
                    }}
                  />
                  {errors.accountCategoryTypeId && (
                    <h4 className="text-error">
                      {errors.accountCategoryTypeId}
                    </h4>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label>
                    {<GetLanguageString props="settings_code" /> || "Code"}
                  </Label>
                  <Input
                    type="text"
                    name="code"
                    className={isUrdu(values.code) ? "ur no-drag" : "no-drag"}
                    value={values.code}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="white" onClick={toggle}>
                  {<GetLanguageString props="common_cancel" />}
                </Button>
                <Button color="primary" type="submit">
                  {<GetLanguageString props="common_save_changes" />}
                </Button>
              </ModalFooter>
            </Form>
          ) : (
            LoadingSpinner()
          )}
        </Modal>
      </Draggable>
    </div>
  );
};

export default EditFinanceAccountCategory;
